import { Announcer } from './Announcer';
import { Box } from './Box';
import { CanvasObject } from './CanvasObject';
import { BOUNDARY, MAX_VELOCITY, MAX_ZOOM, MIN_ZOOM, SCROLL_SENSITIVITY } from './config';
import { ILink, Link } from './Link';
import { Ring } from './Ring';
import { IBlock } from './temp';

export interface IControllerCallbacks {
  onLink(link: CanvasObject<ILink>): void;
}

export class Controller {



  public mouse: { x?: number; y?: number } = { x: undefined, y: undefined };
  public click: { x?: number; y?: number } = { x: undefined, y: undefined };
  public cameraOffset = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  public cameraZoom = 1;
  public boundary: { x: [number, number], y: [number, number] };
  private isPanning: boolean = false;
  private shift: boolean = false;
  private lastZoom = 1;
  private initialPinchDistance: any = null;
  private dragStart = { x: 0, y: 0 };
  private _objects: { [id: string]: CanvasObject<IBlock> } = {};
  private _links: Link[] = [];

  // First click is selected
  private _selected: CanvasObject<IBlock> | null = null;

  // Second click is link (for moving)
  private _link: CanvasObject<IBlock> | null = null;

  private announcer: Announcer;


  constructor(
    canvas: HTMLCanvasElement,
    c: CanvasRenderingContext2D,
    blocks: IBlock[],
    public callback: IControllerCallbacks
  ) {
    this.boundary = BOUNDARY();

    document.addEventListener('keydown', (e) => {
      console.log('Down:', e.key);
      if (e.key === 'Escape') {
        this._link = null;
        this._selected = null;
        this.isPanning = false;
        this.click.x = undefined;
        this.click.y = undefined;
      }
      if (e.key === 'Shift') {
        this.shift = true;
        this._selected = null;
        this._link = null;
      }
    });
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Shift') {
        this.shift = false;
        this._selected = null;
        this._link = null;
      }
    });

    canvas.addEventListener('mousedown', (e: any) => {
      this.click = this.getTransformedPoint(c, e.x, e.y);
      this.onPointerDown(e);
    });
    canvas.addEventListener('touchstart', (e: any) => this.handleTouch(e, this.onPointerDown));
    canvas.addEventListener('mouseup', (e: any) => this.onPointerUp(e));
    canvas.addEventListener('touchend', (e: any) => this.handleTouch(e, this.onPointerUp));
    canvas.addEventListener('mousemove', (e: any) => {
      this.mouse = this.getTransformedPoint(c, e.x, e.y);
      this.onPointerMove(e);
    });
    canvas.addEventListener('touchmove', (e: any) => this.handleTouch(e, this.onPointerMove));
    canvas.addEventListener('wheel', (e: any) => this.adjustZoom(e.deltaY * SCROLL_SENSITIVITY));
    this.init(c, blocks);
    this.announcer = new Announcer(c, 0, 0, {message: 'This is a test message'});
  }

  public init(c: CanvasRenderingContext2D, blocks: IBlock[]) {
    // Layout blocks
    this._objects = {};
    this._links = [];
    for (const block of blocks) {
      var radius = Math.random() * 255 + 5;
      var x = Math.random() * (window.innerWidth - radius * 2) + radius;
      var y = Math.random() * (window.innerHeight - radius * 2) + radius;
      var dx = (Math.random() - 0.5) * MAX_VELOCITY;
      var dy = (Math.random() - 0.5) * MAX_VELOCITY;
      var ring = new Ring(x, y, radius / 2);
      this._objects[block.id] = new Box(c, radius, radius, x, y, dx, dy, ring, block);
    }
    Object.keys(this._objects).forEach((id: string) => {
      this._objects[id].meta.children.forEach((child: string) => {
        if (this._objects.hasOwnProperty(child)) {
          this._links.push(new Link(this._objects[id], this._objects[child], {from: id, to: child, text: 'Text'}));
        } else {
          console.error('Could not find child', child);
        }
      });
    });
    console.log(this._objects);
    console.log(this._links);
  }

  public update(canvas: HTMLCanvasElement, c: CanvasRenderingContext2D): void {
    // c.clearRect(0, 0, canvas.width, canvas.height);
    if (this.mouse.x && this.mouse.y) {
      c.beginPath();
      c.fillStyle = '#FFF';
      if (this.shift) {
        c.rect(this.mouse.x - 2.5, this.mouse.y - 10, 5, 20); // vert
        c.rect(this.mouse.x - 10, this.mouse.y - 2.5, 20, 5); // horizontal
      } else {
        c.arc(this.mouse.x, this.mouse.y, 10, 0, 2 * Math.PI);
      }
      c.fill();
    }
    this._links.forEach((link: CanvasObject<ILink>) => {
      link.update(this, canvas, c);
    });
    Object.keys(this._objects).forEach((id: string) => {
      this._objects[id].update(this, canvas, c);
    });
    this.announcer.update(this, canvas, c);
    if (this._link) {
      if (!this.mouse.x || !this.mouse.y) {
        console.error('Cant render active link, no mouse event data');
      } else {
        // Render active link
        c.beginPath();
        c.lineWidth = 5;
        c.shadowBlur = 0;
        c.moveTo(this._link.x, this._link.y);
        c.lineTo(this.mouse.x, this.mouse.y);
        c.strokeStyle = '#ccffda';
        c.stroke();
      }
    }
  }

  public clickLink(target: CanvasObject<ILink>) {
    this.callback.onLink(target);
    this.click.x = undefined;
    this.click.y = undefined;
    this.isPanning = false;
  }

  public clickObject(target: CanvasObject<IBlock>) {
    if (!this.shift) {
      this._link = null;
      if (this._selected) {
        this._selected = null;
      } else {
        this._selected = target;
      }
    } else {
      this._selected = null;
      if (this._link && this._link.meta.id !== target.meta.id) {
        this._links.push(new Link(this._link, target, { text: 'Test', from: this._link.meta.id }));
        this._link = null;
        this._selected = null;
      } else if (this._link && this._link.meta.id === target.meta.id) {
        this._link = null;
      } else {
        this._link = target;
      }
    }
    this.click.x = undefined;
    this.click.y = undefined;
    this.isPanning = false;
  }

  // public clickObject(target: CanvasObject<IBlock>) {
  //   if (this._selected && this._selected.meta.id === target.meta.id) {
  //     this._selected = null;
  //     this._link = null;
  //   } else if (this._link) {
  //     if (this._link.meta.id === target.meta.id) {
  //       // second click, select
  //       this._selected = target;
  //       this._link = null;
  //     } else {
  //       // link to target
  //       this._links.push(new Link(this._link, target, { text: 'Test' }));
  //       this._link = null;
  //       this._selected = null;
  //     }
  //   } else {
  //     // Set link (first click)
  //     this._link = target;
  //   }
  //   this.click.x = undefined;
  //   this.click.y = undefined;
  // }

  public get link() {
    return this._link;
  }

  public get selected() {
    return this._selected;
  }

  // Gets the relevant location from a mouse or single touch event
  private getEventLocation(e: any) {
    if (e.touches && e.touches.length == 1) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.clientX && e.clientY) {
      return { x: e.clientX, y: e.clientY };
    } else {
      return { x: 0, y: 0 };
    }
  }

  private onPointerDown(e: any) {
    if (!this._selected) {
      this.isPanning = true;
      this.dragStart.x = this.getEventLocation(e).x / this.cameraZoom - this.cameraOffset.x;
      this.dragStart.y = this.getEventLocation(e).y / this.cameraZoom - this.cameraOffset.y;
    }
  }

  private onPointerUp(e: any) {
    this.isPanning = false;
    this._selected = null;
    this.initialPinchDistance = null;
    this.lastZoom = this.cameraZoom;

    // Let the link land!
    if (this._link) {
      this.click.x = e.x;
      this.click.y = e.y;
    }
  }

  private onPointerMove(e: any) {
    if (this.isPanning && !this._selected) {
      this.cameraOffset.x = this.getEventLocation(e).x / this.cameraZoom - this.dragStart.x;
      this.cameraOffset.y = this.getEventLocation(e).y / this.cameraZoom - this.dragStart.y;
    }
  }

  private handleTouch(e: any, singleTouchHandler: any) {
    if (e.touches.length == 1) {
      singleTouchHandler(e);
    } else if (e.type == 'touchmove' && e.touches.length == 2) {
      this.isPanning = false;
      this.handlePinch(e);
    }
  }


  private handlePinch(e: any) {
    e.preventDefault();

    let touch1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    let touch2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };

    // This is distance squared, but no need for an expensive sqrt as it's only used in ratio
    let currentDistance = (touch1.x - touch2.x) ** 2 + (touch1.y - touch2.y) ** 2;

    if (this.initialPinchDistance == null) {
      this.initialPinchDistance = currentDistance;
    } else {
      console.error('Pinch disabled');
      // this.adjustZoom(null, currentDistance / this.initialPinchDistance);
    }
  }

  private adjustZoom(zoomAmount: any, zoomFactor?: any) {
    if (!this.isPanning) {
      if (zoomAmount) {
        this.cameraZoom += zoomAmount;
      } else if (zoomFactor) {
        console.log(zoomFactor);
        this.cameraZoom = zoomFactor * this.lastZoom;
      }

      this.cameraZoom = Math.min(this.cameraZoom, MAX_ZOOM);
      this.cameraZoom = Math.max(this.cameraZoom, MIN_ZOOM);

      console.log(zoomAmount);
    }
  }

  private getTransformedPoint(c: CanvasRenderingContext2D, x: number, y: number) {
    const inverseTransform = c.getTransform().invertSelf();
    const transformedX = inverseTransform.a * x + inverseTransform.c * y + inverseTransform.e;
    const transformedY = inverseTransform.b * x + inverseTransform.d * y + inverseTransform.f;

    return { x: transformedX, y: transformedY };
  }

}
